'use client';

import React, { forwardRef, useEffect } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useCategoryScoringData } from '@/hooks/useCategoryScoringData';
import { useCertificationScores } from '@/hooks/useCertificationScores';
import { useScoreCalculations } from '@/hooks/useScoreCalculations';
import { useScoreStatistics } from '@/hooks/useScoreStatistics';
import { useScoreSave } from '@/contexts/ScoreSaveContext';
import { CategorySection } from './category-scoring/CategorySection';
import { ScoreComparison } from './ScoreComparison';

interface ClusterSectionProps {
    certificationId: string;
    cluster: 'ENVIRONMENT' | 'DESIGN' | 'ECONOMIC' | 'SOCIAL';
    isEnabled?: boolean;
    activeWeightProfile?: any;
    isReadOnly?: boolean;
}

// Removed static CLUSTER_LABELS in favor of translations

const CLUSTER_COLORS: Record<string, { bg: string; hoverBg: string; border: string; text: string; accent: string }> = {
    'ENVIRONMENT': { bg: 'bg-emerald-50', hoverBg: 'hover:bg-emerald-100', border: 'border-emerald-200', text: 'text-emerald-700', accent: 'from-emerald-500 to-green-500' },
    'DESIGN': { bg: 'bg-blue-50', hoverBg: 'hover:bg-blue-100', border: 'border-blue-200', text: 'text-blue-700', accent: 'from-blue-500 to-indigo-500' },
    'ECONOMIC': { bg: 'bg-amber-50', hoverBg: 'hover:bg-amber-100', border: 'border-amber-200', text: 'text-amber-700', accent: 'from-amber-500 to-orange-500' },
    'SOCIAL': { bg: 'bg-rose-50', hoverBg: 'hover:bg-rose-100', border: 'border-rose-200', text: 'text-rose-700', accent: 'from-rose-500 to-pink-500' },
};

const ClusterSection = forwardRef<HTMLDivElement, ClusterSectionProps>(
    ({ certificationId, cluster, isEnabled = true, activeWeightProfile, isReadOnly = false }, ref) => {
        const locale = useLocale();
        const t = useTranslations('certification.stage2');
        const colors = CLUSTER_COLORS[cluster];

        // 1. Data Loading Hook
        const {
            categoryData, allCategoryData, weightsData, allWeightsData, loading: loadingData,
            getGlossaryDefinition, toggleCategory, expandedCategories, enNameMap
        } = useCategoryScoringData(cluster, locale);

        // 2. Score Management Hook
        const {
            scores, allScores, saving, loadingScores, handleScoreChange, saveScores, hasUnsavedScores, getScoreKey
        } = useCertificationScores(certificationId, cluster, enNameMap);

        // 3. Score Calculations Hook
        const {
            getScoreColor, progress, categoryScores, clusterScore, overallScore
        } = useScoreCalculations({
            scores, allScores, categoryData, weightsData, allWeightsData, allCategoryData, enNameMap, getScoreKey, activeWeightProfile
        });

        // 4. Score Statistics Hook (for benchmark comparisons)
        const {
            getSubcategoryStats, getCategoryStats, getClusterStats, getOverallStats
        } = useScoreStatistics();

        // 5. Score Save Context - Register this cluster's save function
        const { registerSaveFunction, unregisterSaveFunction, registerHasUnsaved, unregisterHasUnsaved } = useScoreSave();

        useEffect(() => {
            registerSaveFunction(cluster, saveScores);
            return () => unregisterSaveFunction(cluster);
        }, [cluster, saveScores, registerSaveFunction, unregisterSaveFunction]);

        useEffect(() => {
            registerHasUnsaved(cluster, hasUnsavedScores);
            // Clean up by removing this cluster's status when the component unmounts
            // or when hasUnsavedScores changes (will be immediately re-added with new value)
            return () => unregisterHasUnsaved(cluster);
        }, [cluster, hasUnsavedScores, registerHasUnsaved, unregisterHasUnsaved]);

        const clusterStats = getClusterStats(cluster);
        const overallStats = getOverallStats();

        // Helper to get stats using English names (mapped from local names)
        const getCategoryStatsWrapped = (cluster: string, localCategoryName: string) => {
            const mapEntry = enNameMap.get(`${localCategoryName}::${categoryData.find(c => c.category === localCategoryName)?.subcategories[0]?.name}`);
            const enCategoryName = mapEntry ? mapEntry.enCategory : localCategoryName;
            return getCategoryStats(cluster, enCategoryName);
        };

        const getSubcategoryStatsWrapped = (cluster: string, localCategoryName: string, localSubcategoryName: string) => {
            const mapEntry = enNameMap.get(`${localCategoryName}::${localSubcategoryName}`);
            const enCategoryName = mapEntry ? mapEntry.enCategory : localCategoryName;
            const enSubcategoryName = mapEntry ? mapEntry.enSubcategory : localSubcategoryName;
            return getSubcategoryStats(cluster, enCategoryName, enSubcategoryName);
        };

        const isLoading = loadingData || loadingScores;

        // Helper to toggle all categories in this cluster
        const toggleAllCategories = () => {
            const allExpanded = categoryData.every(cat => expandedCategories.has(cat.category));
            categoryData.forEach(cat => {
                if (allExpanded) {
                    // If all are expanded, collapse all
                    if (expandedCategories.has(cat.category)) {
                        toggleCategory(cat.category);
                    }
                } else {
                    // If any are collapsed, expand all
                    if (!expandedCategories.has(cat.category)) {
                        toggleCategory(cat.category);
                    }
                }
            });
        };

        // Helper function to get weights
        const getCategoryWeight = (localCategoryName: string): number => {
            // First try active weight profile if available
            if (activeWeightProfile && activeWeightProfile.weight_tree) {
                // Find cluster node using EN name logic. Note: Clusters are top level.
                // Assuming activeWeightProfile.weight_tree contains top level items which are CLUSTERS.
                // We map local 'cluster' const string (e.g. 'ENVIRONMENT') to matching node in tree.
                const clusterNode = activeWeightProfile.weight_tree.find((n: any) =>
                    n.key === cluster || n.name_en?.toUpperCase() === cluster
                );

                if (clusterNode) {
                    if (clusterNode.weighting === 0) return 0;

                    // Map local category name to EN name using map
                    // Similar logic to static weights
                    const mapEntry = enNameMap.get(`${localCategoryName}::${categoryData.find(c => c.category === localCategoryName)?.subcategories[0]?.name}`);
                    const enCategoryName = mapEntry ? mapEntry.enCategory : localCategoryName;

                    const categoryNode = clusterNode.children?.find((c: any) => c.name_en === enCategoryName);
                    if (categoryNode) {
                        return categoryNode.weighting;
                    }
                }
            }

            // Fallback to static weights if no active profile or not found
            if (!weightsData) return 1;
            if (weightsData.weighting === 0) return 0;

            const mapEntry = enNameMap.get(`${localCategoryName}::${categoryData.find(c => c.category === localCategoryName)?.subcategories[0]?.name}`);
            const enCategoryName = mapEntry ? mapEntry.enCategory : localCategoryName;

            const categoryNode = weightsData.categories.find(c => c.name === enCategoryName);
            return categoryNode ? categoryNode.weighting : 1;
        };

        const getSubcategoryWeight = (localCategoryName: string, localSubcategoryName: string): number => {
            // First try active weight profile if available
            if (activeWeightProfile && activeWeightProfile.weight_tree) {
                const clusterNode = activeWeightProfile.weight_tree.find((n: any) =>
                    n.key === cluster || n.name_en?.toUpperCase() === cluster
                );

                if (clusterNode) {
                    if (clusterNode.weighting === 0) return 0;

                    const mapEntry = enNameMap.get(`${localCategoryName}::${localSubcategoryName}`);
                    const enCategoryName = mapEntry ? mapEntry.enCategory : localCategoryName;
                    const enSubcategoryName = mapEntry ? mapEntry.enSubcategory : localSubcategoryName;

                    const categoryNode = clusterNode.children?.find((c: any) => c.name_en === enCategoryName);
                    if (categoryNode) {
                        if (categoryNode.weighting === 0) return 0;
                        const subcategoryNode = categoryNode.children?.find((s: any) => s.name_en === enSubcategoryName);
                        if (subcategoryNode) {
                            return subcategoryNode.weighting;
                        }
                    }
                }
            }

            // Fallback
            if (!weightsData) return 1;
            if (weightsData.weighting === 0) return 0;

            const mapEntry = enNameMap.get(`${localCategoryName}::${localSubcategoryName}`);
            const enCategoryName = mapEntry ? mapEntry.enCategory : localCategoryName;
            const enSubcategoryName = mapEntry ? mapEntry.enSubcategory : localSubcategoryName;

            const categoryNode = weightsData.categories.find(c => c.name === enCategoryName);
            if (!categoryNode) return 1;
            if (categoryNode.weighting === 0) return 0;

            const subcategoryNode = categoryNode.subcategories.find(s => s.name === enSubcategoryName);
            return subcategoryNode ? subcategoryNode.weighting : 1;
        };

        if (isLoading) {
            return (
                <div ref={ref} className={`border ${colors.border} ${colors.bg} p-4 animate-pulse scroll-mt-[70px]`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="h-6 w-40 bg-slate-200" />
                        <div className="h-4 w-24 bg-slate-200" />
                    </div>
                    <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="border border-slate-200 p-3 bg-white">
                                <div className="h-4 w-48 bg-slate-200" />
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <div ref={ref} id={`section-cluster-${cluster.toLowerCase()}`} className="scroll-mt-[70px]">
                {/* Cluster Header */}
                <div
                    className={`${colors.bg} ${colors.hoverBg} px-4 py-3 flex items-center justify-between border ${colors.border} cursor-pointer transition-colors relative z-10`}
                    onClick={toggleAllCategories}
                >
                    <div className="flex items-center gap-3">
                        <h3 className={`text-lg font-semibold ${colors.text}`}>
                            {t(cluster.toLowerCase() as any)}
                        </h3>
                        <div className="flex items-center gap-3">
                            {/* Stats next to title */}
                            {clusterStats && (
                                <div onClick={(e) => e.stopPropagation()}>
                                    <ScoreComparison userScore={clusterScore} stats={clusterStats} compact />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        {/* Progress bar */}
                        <div className="flex items-center gap-2">
                            <div className="w-32 bg-white/50 h-2">
                                <div
                                    className={`bg-gradient-to-r ${colors.accent} h-2 transition-all duration-300`}
                                    style={{ width: `${progress.percentage}%` }}
                                />
                            </div>
                            <span className="text-xs text-black">
                                {progress.scored}/{progress.total}
                            </span>
                        </div>

                        {/* Cluster Score moved here */}
                        <div className="flex items-center gap-2">
                            <span className={`text-2xl font-bold ${colors.text}`}>
                                {clusterScore !== null ? clusterScore.toFixed(1) : '—'}
                            </span>
                        </div>

                        {/* Saving spinner */}
                        {saving && (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-200 border-t-black" />
                        )}
                    </div>
                </div>

                {/* Categories */}
                <div className={`space-y-3 mt-3 relative ${!isEnabled ? 'opacity-50 pointer-events-none grayscale-[50%]' : ''}`}>
                    {!isEnabled && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center p-6 text-center">
                            <div className="bg-white/90 backdrop-blur-md p-6 shadow-xl border border-slate-200 max-w-sm">
                                <div className="w-12 h-12 bg-amber-100 flex items-center justify-center mx-auto mb-4 text-amber-600">
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <h4 className="text-black font-semibold mb-2">{t('scoringRestricted')}</h4>
                                <p className="text-sm text-black" dangerouslySetInnerHTML={{
                                    __html: t.raw('scoringRestrictedDesc')
                                }} />
                            </div>
                        </div>
                    )}
                    {categoryData.map((category) => (
                        <CategorySection
                            key={category.category}
                            category={category}
                            cluster={cluster}
                            isExpanded={expandedCategories.has(category.category)}
                            toggleCategory={toggleCategory}
                            categoryScore={categoryScores[category.category]}
                            glossaryDefinition={
                                category.info
                                    ? { cluster: '', terminology: category.category, definition: category.info }
                                    : getGlossaryDefinition(category.category)
                            }
                            scores={scores}
                            getScoreKey={getScoreKey}
                            onScoreChange={handleScoreChange}
                            getScoreColor={getScoreColor}
                            getGlossaryDefinition={getGlossaryDefinition}
                            getCategoryStats={getCategoryStatsWrapped}
                            getSubcategoryStats={getSubcategoryStatsWrapped}
                            getCategoryWeight={getCategoryWeight}
                            getSubcategoryWeight={getSubcategoryWeight}
                            isReadOnly={isReadOnly}
                        />
                    ))}
                </div>
            </div>
        );
    }
);

ClusterSection.displayName = 'ClusterSection';

interface AllClustersScoringProps {
    certificationId: string;
    isBuildingTypeSelected?: boolean;
    activeWeightProfile?: any;
    environmentRef?: React.RefObject<HTMLDivElement | null>;
    designRef?: React.RefObject<HTMLDivElement | null>;
    economicRef?: React.RefObject<HTMLDivElement | null>;
    socialRef?: React.RefObject<HTMLDivElement | null>;
    // Optional: when set, only render this specific cluster (for page mode)
    activeCluster?: 'ENVIRONMENT' | 'DESIGN' | 'ECONOMIC' | 'SOCIAL' | null;
    isReadOnly?: boolean;
}

export function AllClustersScoring({
    certificationId,
    isBuildingTypeSelected = true,
    activeWeightProfile,
    environmentRef,
    designRef,
    economicRef,
    socialRef,
    activeCluster = null, // null means render all clusters
    isReadOnly = false,
}: AllClustersScoringProps) {
    const clusterRefs: Record<string, React.RefObject<HTMLDivElement | null> | undefined> = {
        'ENVIRONMENT': environmentRef,
        'DESIGN': designRef,
        'ECONOMIC': economicRef,
        'SOCIAL': socialRef,
    };

    const allClusters: ('ENVIRONMENT' | 'DESIGN' | 'ECONOMIC' | 'SOCIAL')[] = [
        'ENVIRONMENT',
        'DESIGN',
        'ECONOMIC',
        'SOCIAL',
    ];

    // If activeCluster is specified, only show that cluster
    const clusters = activeCluster ? [activeCluster] : allClusters;

    return (
        <div className="space-y-6">
            {clusters.map((cluster) => (
                <ClusterSection
                    key={cluster}
                    ref={clusterRefs[cluster]}
                    certificationId={certificationId}
                    cluster={cluster}
                    isEnabled={isBuildingTypeSelected}
                    activeWeightProfile={activeWeightProfile}
                    isReadOnly={isReadOnly}
                />
            ))}
        </div>
    );
}

